import React, { useRef, useState, useEffect } from "react"
import styled from "styled-components"
import gsap from "gsap"

import media from "assets/styles/media"
import colors from "assets/styles/colors"
import text from "assets/styles/text"

import PlayButton from "components/buttons/PlayButton"

import { ReactComponent as CloseSVG } from "images/global/close.svg"
import { ReactComponent as ChevronSVG } from "images/global/chevron.svg"
import { ReactComponent as PlaySVG } from "images/global/play.svg"
import { ReactComponent as PauseSVG } from "images/global/pause.svg"
import { useMedia } from "utils/hooks"
import { addDebouncedEventListener } from "utils/functions"

type ResponsiveProps = {
  children: (JSX.Element | string)[]
  onClick: VoidFunction
  left?: boolean
  right?: boolean
}

const ResponsiveButton: React.FC<ResponsiveProps> = ({
  children,
  onClick,
  left,
  right,
}) => {
  return (
    <Button $left={left} $right={right} onClick={() => onClick()}>
      {children}
    </Button>
  )
}

const Button = styled.div<{ $left?: boolean; $right?: boolean }>`
  width: 25%;
  display: flex;
  justify-content: center;
  align-items: center;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid black;
  border-right: none;
  background: white;
  color: ${colors.black800};
  ${text.tablet.buttonText};
  line-height: 1.95vw;

  height: 100%;
  svg {
    transform: rotate(
      ${props => (props.$left ? "-90deg" : props.$right ? "90deg" : "0")}
    );
  }

  ${media.mobile} {
    ${text.mobile.buttonText};
  }
`

type Props = {
  poster: string
  video: string
  index: number
  scrollTo: Function
  total: number
  name: string
  title: string
  globalFullScreen: false | number
  setGlobalFullScreen: Function
}

const VideoPlayer: React.FC<Props> = ({
  poster,
  video,
  scrollTo,
  index,
  total,
  name,
  title,
  globalFullScreen,
  setGlobalFullScreen,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const wrapperRef = useRef(null)
  const [play, setPlay] = useState<boolean>(false)
  const [fullScreen, setFullScreen] = useState<false | number>(false)
  const [fullScreenCheck, setFullScreenCheck] = useState(false)
  const videoHeight = useMedia("0", "0", "56.25vw", "56.53vw")
  const videoWidth = useMedia("0", "0", "79vw", "80vw")
  const videoY = useMedia(
    "",
    "",
    "34.18vw",
    `${(window.innerHeight / 100) * 36.945}`
  )
  const videoHeightClosed = useMedia("", "", "100%", "77.586%")
  const [actualHeight, setActualHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight + "px" : "100vh"
  )

  const initialPlay = () => {
    setPlay(true)
    setGlobalFullScreen(index)
  }

  const handleClick = () => {
    if (fullScreenCheck) {
      setPlay(play => !play)
    }
  }

  const handleNext = () => {
    if (index + 1 === total) {
      handleClose()
    } else {
      setPlay(false)
      scrollTo(index + 1)
    }
  }

  const handlePrevious = () => {
    if (index === 0) {
      handleClose()
    } else {
      setPlay(false)
      scrollTo(index - 1)
    }
  }

  const handleClose = () => {
    setGlobalFullScreen(false)
    setPlay(false)
  }
  useEffect(() => {
    const check = globalFullScreen || globalFullScreen === 0 ? true : false
    setFullScreen(globalFullScreen)
    setFullScreenCheck(check)
  }, [globalFullScreen])

  useEffect(() => {
    if (fullScreenCheck) {
      window.locomotiveScroll.stop()
      if (index === fullScreen) {
        scrollTo(index)
      }

      gsap.to(wrapperRef.current, {
        width: "100vw",
        height: actualHeight,
        duration: 1,
      })

      gsap.to(videoRef.current, {
        duration: 1,
        width: "100%",
        height: videoHeight,
        objectFit: "contain",
        opacity: 1,
        y: videoY,
      })
      const headerCheck = document.getElementById("header")
      if (headerCheck) {
        gsap.to("#header", {
          duration: 1,
          y: "-200%",
        })
      }
      const sideNavCheck = document.getElementById("sidenav-open")
      if (sideNavCheck) {
        gsap.to("#sidenav-open", {
          duration: 1,
          x: "-100%",
        })
      }
    } else {
      window.locomotiveScroll.start()

      gsap.to(videoRef.current, {
        duration: 1,
        width: videoWidth,
        height: videoHeightClosed,
        cursor: "auto",
        objectFit: "cover",
        opacity: 0.6,
        y: 0,
      })
      const headerCheck = document.getElementById("header")
      if (headerCheck) {
        gsap.to("#header", {
          duration: 1,
          y: 0,
        })
      }
      const sideNavCheck = document.getElementById("sidenav-open")
      if (sideNavCheck) {
        gsap.to("#sidenav-open", {
          duration: 1,
          x: 0,
        })
      }
    }
  }, [fullScreenCheck])

  useEffect(() => {
    if (videoRef.current) {
      if (play) {
        videoRef.current.play()
      } else {
        videoRef.current.pause()
      }
    }
  }, [play])

  useEffect(() => {
    function updateUnits() {
      if (typeof window !== "undefined") {
        setActualHeight(window.innerHeight + "px")
      } else {
        setActualHeight("100vh")
      }
    }

    updateUnits()
    const removeListener = addDebouncedEventListener(
      window,
      "resize",
      updateUnits,
      100
    )

    return removeListener
  }, [])

  return (
    <Wrapper ref={wrapperRef} actualHeight={actualHeight}>
      {!fullScreenCheck && <PlayButton onClick={initialPlay} />}

      <Video ref={videoRef} poster={poster} onClick={handleClick} playsInline>
        <source src={video} type="video/mp4" />
      </Video>

      {!fullScreenCheck && <Name>{name}</Name>}
      {!fullScreenCheck && <Title>{title}</Title>}

      {fullScreenCheck && (
        <Controls>
          <ResponsiveButton right onClick={handlePrevious}>
            <ChevronLeft /> Prev{" "}
          </ResponsiveButton>
          <ResponsiveButton onClick={handleClick}>
            {play ? "Pause" : "Play"}
            <FauxPlay playing={play}>
              <Play /> <Pause />
            </FauxPlay>
          </ResponsiveButton>
          <ResponsiveButton onClick={handleClose}>
            Close <Close />
          </ResponsiveButton>
          <ResponsiveButton left onClick={handleNext}>
            Next <ChevronRight />
          </ResponsiveButton>
        </Controls>
      )}
    </Wrapper>
  )
}

export default VideoPlayer

const Wrapper = styled.div<{ actualHeight: string }>`
  height: ${props => props.actualHeight};
  width: 100vw;
  position: relative;
  filter: grayscale(100);
`

const Video = styled.video`
  object-fit: cover;
  height: 100%;
  width: 79vw;
  opacity: 0.6;
  ${media.mobile} {
    position: absolute;
    top: 0;
    height: 77.586%;
    width: 80vw;
    top: 0;
  }
`

const Close = styled(CloseSVG)`
  width: 1.95vw;
  height: 1.95vw;
  color: ${colors.black800};
  line-height: 1.95vw;
  margin-left: 0.98vw;
  ${media.mobile} {
    width: 5.33vw;
    height: 5.33vw;
  }
`
const Play = styled(PlaySVG)`
  width: 1.95vw;
  height: 1.95vw;
  right: 0;
  position: absolute;
  opacity: 0;
  transition: 0.3s;
  ${media.mobile} {
    width: 5.33vw;
    height: 5.33vw;
    right: auto;
  }
`
const Pause = styled(PauseSVG)`
  width: 1.95vw;
  height: 1.95vw;
  position: relative;
  transition: 0.3s;
  ${media.mobile} {
    width: 5.33vw;
    height: 5.33vw;
  }
`
const FauxPlay = styled.div<{ playing: boolean }>`
  position: relative;
  margin-left: 0.98vw;
  ${Pause} {
    opacity: ${props => (props.playing ? 1 : 0)};
  }
  ${Play} {
    opacity: ${props => (props.playing ? 0 : 1)};
  }
`

const ChevronRight = styled(ChevronSVG)`
  width: 1.95vw;
  height: 1.95vw;
  margin-left: 0.98vw;
  ${media.mobile} {
    width: 5.33vw;
    height: 5.33vw;
  }
`

const ChevronLeft = styled(ChevronSVG)`
  width: 1.95vw;
  height: 1.95vw;
  margin-right: 0.98vw;
  ${media.mobile} {
    width: 5.33vw;
    height: 5.33vw;
  }
`

const Name = styled.p`
  ${text.tablet.h3}
  width: 63.477vw;
  font-size: 5.859vw;
  line-height: 6.445vw;
  color: ${colors.white};
  position: absolute;
  z-index: 2;
  bottom: 47.266vw;
  left: 29.297vw;
  letter-spacing: inherit;
  ${media.mobile} {
    left: 5.07vw;
    bottom: 77.333vw;
    ${text.mobile.h5};
    width: 77.333vw;
    font-size: 8vw;
  }
`

const Title = styled.p`
  ${text.tablet.h5}
  color: ${colors.blue100};
  position: absolute;
  z-index: 2;
  left: 41.99vw;
  bottom: 39.06vw;
  ${media.mobile} {
    ${text.mobile.bodyL};
    bottom: 84.27vw;
    left: 5.07vw;
  }
`

const Controls = styled.div`
  width: 100%;
  height: 5.86vw;
  display: flex;
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;

  ${media.mobile} {
    height: 16vw;
  }
`
